import { Platform } from 'react-native';

/**
 * CSV Export Utility for Products
 * Matches product structure with all fields
 */

export interface ProductCSVRow {
  name: string;
  description?: string;
  brand?: string;
  sku?: string;
  barcode?: string;
  price: string;
  currency?: string;
  salePrice?: string;
  costPrice?: string;
  inventoryQuantity?: string;
  inventoryTracking?: string;
  lowStockThreshold?: string;
  availability?: string;
  condition?: string;
  weight?: string;
  'dimensions-length'?: string;
  'dimensions-width'?: string;
  'dimensions-height'?: string;
  categoryIds?: string;
  hasVariants?: string;
}

/**
 * Convert products array to CSV format
 */
export const productsToCSV = (products: any[]): string => {
  // CSV Headers matching product structure
  const headers = [
    'name',
    'description',
    'brand',
    'sku',
    'barcode',
    'price',
    'currency',
    'salePrice',
    'costPrice',
    'inventoryQuantity',
    'inventoryTracking',
    'lowStockThreshold',
    'availability',
    'condition',
    'weight',
    'dimensions-length',
    'dimensions-width',
    'dimensions-height',
    'categoryIds',
    'hasVariants',
  ];

  // Build CSV rows
  const rows = products.map((product) => {
    const row: ProductCSVRow = {
      name: product.name || '',
      description: product.description || '',
      brand: product.brand || '',
      sku: product.sku || '',
      barcode: product.barcode || '',
      price: product.price?.toString() || '0',
      currency: product.currency || 'USD',
      salePrice: product.salePrice?.toString() || '',
      costPrice: product.costPrice?.toString() || '',
      inventoryQuantity: product.inventoryQuantity?.toString() || '',
      inventoryTracking: product.inventoryTracking ? 'true' : 'false',
      lowStockThreshold: product.lowStockThreshold?.toString() || '',
      availability: product.availability || 'in_stock',
      condition: product.condition || 'new',
      weight: product.weight?.toString() || '',
      'dimensions-length': product.dimensions?.length?.toString() || '',
      'dimensions-width': product.dimensions?.width?.toString() || '',
      'dimensions-height': product.dimensions?.height?.toString() || '',
      categoryIds: Array.isArray(product.categoryIds) ? product.categoryIds.join('|') : '',
      hasVariants: product.hasVariants ? 'true' : 'false',
    };

    return row;
  });

  // Convert to CSV string
  const csvRows = [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((header) => {
        const value = row[header as keyof ProductCSVRow] || '';
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    ),
  ];

  return csvRows.join('\n');
};

/**
 * Generate empty CSV template with headers and example row
 */
export const generateEmptyProductCSVTemplate = (): string => {
  const headers = [
    'name',
    'description',
    'brand',
    'sku',
    'barcode',
    'price',
    'currency',
    'salePrice',
    'costPrice',
    'inventoryQuantity',
    'inventoryTracking',
    'lowStockThreshold',
    'availability',
    'condition',
    'weight',
    'dimensions-length',
    'dimensions-width',
    'dimensions-height',
    'categoryIds',
    'hasVariants',
  ];

  const exampleRow = [
    'Sample Product',
    'This is a sample product description',
    'Sample Brand',
    'SKU-001',
    '1234567890123',
    '99.99',
    'USD',
    '79.99',
    '50.00',
    '100',
    'true',
    '10',
    'in_stock',
    'new',
    '500',
    '10',
    '5',
    '3',
    'category1|category2',
    'false',
  ];

  return [headers.join(','), exampleRow.join(',')].join('\n');
};

/**
 * Download CSV file (web only for now)
 */
export const downloadProductCSV = (csvContent: string, filename: string = 'products.csv'): void => {
  if (Platform.OS === 'web') {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else {
    // TODO: Implement mobile download using expo-file-system or similar
    console.warn('Mobile CSV download not yet implemented');
  }
};
